import { useCallback, useMemo, useState } from "react";

export interface UseClientSidePaginationResult<TRow> {
  /** The slice of rows belonging to the displayed page. */
  pageRows: TRow[];
  /** Always at least 1, so an empty table still renders as a single (hidden) page. */
  totalPages: number;
  /** The page actually on screen: `currentPage` clamped to the rows that exist. */
  displayedPage: number;
  setCurrentPage: (page: number) => void;
  /** Send the user back to page 1 after a re-sort or a filter change reorders the row set. */
  resetToFirstPage: () => void;
}

/**
 * Paginates an already-sorted, already-filtered row set in memory.
 *
 * For tables that load every row up front. Callers sort and filter first and pass the result in, so
 * the ordering the user asked for holds across the whole set instead of shuffling within each page.
 *
 * The page is clamped while rendering rather than corrected in an effect. A shrinking row set (a
 * narrower filter, a refresh) therefore never gets a render where `currentPage` points past the end
 * and the table shows nothing -- the clamp holds during the shrink render itself. `currentPage` is
 * deliberately left unclamped in state: it is the page the user last chose, and it becomes visible
 * again if the row set grows back.
 *
 * Server-paginated tables want `@/utils/hooks/use-pagination` instead, which keeps the page in the
 * URL and drives a refetch.
 */
export default function useClientSidePagination<TRow>(
  rows: TRow[],
  pageSize: number,
): UseClientSidePaginationResult<TRow> {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const displayedPage = Math.min(currentPage, totalPages);

  const pageRows = useMemo(() => {
    const startIndex = (displayedPage - 1) * pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [displayedPage, pageSize, rows]);

  const resetToFirstPage = useCallback(() => setCurrentPage(1), []);

  return { pageRows, totalPages, displayedPage, setCurrentPage, resetToFirstPage };
}
